import { COMMON_CLASS_NAMES } from '../constants';
import { DomUtils } from '../utils/domUtils';
import { DomAnimation } from '../utils/domAnimation';

/**
 * @class Popup
 */
export class Popup {
  /** @type {Element} */
  trigger;
  /** @type {Set<HTMLElement>} */
  otherTriggers = new Set();
  /** @type {Set<HTMLElement>} */
  targets = new Set();
  /** @type {string} */
  ariaHaspopup = 'false'
  transitionState = 'idle'
  /** @type {HTMLElement | null} */
  backdrop

  _scrollTraps = new WeakMap();
  /**
   * Creates an instance of the Popup class.
   *
   * @param {HTMLElement} el - The trigger element that initializes the popup.
   *
   * @property {HTMLElement} trigger - The element that triggers the popup.
   * @property {string} ariaHaspopup - The value of the `aria-haspopup` attribute of the trigger element, defaults to 'false'.
   * @property {HTMLElement[]} targets - An array of target elements referenced by `aria-controls` or `popovertarget` attributes.
   * @property {HTMLElement | null} backdrop - The backdrop element identified by `Popup.ID_NAMES.BACKDROP`.
   * @property {Function} boundDialogClick - A bound function for handling dialog click events.
   * @property {Function} boundKeydown - A bound function for handling keydown events.
   */
  constructor(el) {
    this.trigger = el;

    this.ariaHaspopup = this.trigger.getAttribute('aria-haspopup') || 'false';

    const ariaControls = this.trigger.getAttribute('aria-controls');
    if (ariaControls) {
      ariaControls.split(/\s+/).forEach(id => {
        /** @type { HTMLElement | null } */
        const el = document.getElementById(id);
        el && this.targets.add(el);

        const trigger = document.querySelectorAll(`[aria-controls~="${id}"]`);
        trigger.forEach(trigger => trigger != this.trigger && this.otherTriggers.add(trigger));
      });
    }

    const popoverTarget = this.trigger.getAttribute('popovertarget');
    if (popoverTarget) {
      popoverTarget.split(/\s+/).forEach(id => {
        /** @type { HTMLElement | null } */
        const el = document.getElementById(id);
        el && this.targets.add(el);

        const trigger = document.querySelectorAll(`[popovertarget~="${id}"]`);
        trigger.forEach(trigger => trigger != this.trigger && this.otherTriggers.add(trigger));
      });
    }

    this.backdrop = document.getElementById(Popup.ID_NAMES.BACKDROP);

    this.boundDialogClick = this.handleDialogClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
  }

  static ID_NAMES = {
    BACKDROP: 'backdrop'
  };

  static CLASS_NAMES = {
    IS_TOPLAYER: 'is-toplayer',
    HAS_BACKDROP: 'has-backdrop'
  };


  /**
   * @method isOpen
   * Checks if the component is currently open.
   *
   * Determines if the trigger element has 'aria-expanded' set to 'true',
   * or if any of the target elements are open (via the 'open' attribute or
   * the presence of the ACTIVE class).
   *
   * @returns {boolean} True if the component is open, otherwise false.
   */
  isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true' || Array.from(this.targets.values).some(target => target.hasAttribute('open') || target.classList.has(COMMON_CLASS_NAMES.ACTIVE));
  }


  /**
   * Handles hover and focus events to toggle the visibility of a popup.
   *
   * @param {Event} e - The event object representing the triggered event.
   *   - If the event type is 'mouseenter' or 'focusin', the popup is shown.
   *   - If the event type is 'mouseleave', the popup is hidden.
   *   - If the event type is 'focusout', the popup is hidden only if the
   *     currently focused element is not contained within the trigger element.
   */
  handleHover(e) {
    if (e.type === 'mouseenter' || e.type === 'focusin') {
      this.togglePopup(true);
    } else if (e.type === 'mouseleave') {
      this.togglePopup(false);
    } else if ((e.type === 'focusout')) {
      requestAnimationFrame(() => {
        if (!this.trigger.contains(document.activeElement)) {
          this.togglePopup(false);
        }
      });
    }
  }

  /**
   * @method handleClick
   * Handles the click event for the trigger element.
   * Prevents the event from propagating and performs an action
   * based on the value of the 'popovertargetaction' attribute.
   *
   * @param {Event} e - The click event object.
   */
  handleClick(e) {
    e.stopPropagation();
    switch (this.trigger.getAttribute('popovertargetaction')) {
      case 'show':
        this.togglePopup(true);
        break;

      case 'hide':
        this.togglePopup(false);
        break;

      default:
        this.togglePopup(!this.isOpen());
        break;
    }
  }

  /**
   * @method handleDialogClick
   * Handles the click event on the dialog element.
   * Determines if the click occurred outside the target elements (backdrop click)
   * and toggles the popup visibility accordingly.
   *
   * @param {MouseEvent} e - The mouse event triggered by the click.
    * @returns {boolean} - Returns `true` if the event occurred outside the bounding rectangles of all targets, otherwise `false`.
   */
  handleDialogClick(e) {
    const isValid = Array.from(this.targets.values()).every(target => {
      const rect = target.getBoundingClientRect();
      const isBackdropClick =
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;
      return isBackdropClick === true;
    });
    if (!isValid) return;
    this.togglePopup(false);
  }

  /**
   * Handles keydown events for the component.
   *
   * @param {KeyboardEvent} e - The keyboard event object.
   * @returns {void}
   *
   * @description
   * This method listens for specific key presses and performs actions accordingly:
   * - `Enter`: Toggles the popup based on the `popovertargetaction` attribute of the trigger element.
   * - `Escape`: Closes the popup.
   * - `Tab` or `Shift + Tab`: Manages focus within the popup when it is open, ensuring focus remains
   *   within the popup's focusable elements.
   *
   * @example
   * // Example usage in an event listener
   * element.addEventListener('keydown', (e) => handleKeydown(e));
   */
  handleKeydown(e) {
    e.stopPropagation();
    switch (e.key) {
      case 'Enter':
        if (e.target === this.trigger) {
          switch (this.trigger.getAttribute('popovertargetaction')) {
            case 'show':
              e.preventDefault();
              this.togglePopup(true);
              break;

            case 'hide':
              this.togglePopup(false);
              break;

            default:
              this.togglePopup(!this.isOpen);
              break;
          }
        }
        break;

      case 'Escape':
        this.togglePopup(false);
        break;

      case 'Tab':
        if (this.isOpen()) {
          const focusable = this.targets.flatMap(target => DomUtils.getFocusableElements(target));
          if (focusable.length < 1) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];

          if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus({preventScroll: false, focusVisible: false});
            }
          } else {
            // Tab
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus({preventScroll: false, focusVisible: false});
            }
          }
        }
        break;

      default:
        break;
    }
  }
  /**
   * Prevents scroll events from propagating beyond the popup targets.
   */
  trapScroll(container) {
    const isScrollable = el =>
      el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;

    if (!isScrollable(container)) return;

    const listeners = {
      touchstart: e => {
        container._startY = e.touches[0].clientY;
      },
      touchmove: e => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const currentY = e.touches[0].clientY;

        if (!container._startY) {
          container._startY = currentY;
          return;
        }

        const deltaY = currentY - container._startY;

        if (
          (deltaY > 0 && scrollTop === 0) ||
          (deltaY < 0 && scrollTop + clientHeight >= scrollHeight)
        ) {
          e.preventDefault();
        }
      },
      wheel: e => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const delta = e.deltaY;

        if (
          (delta < 0 && scrollTop === 0) ||
          (delta > 0 && scrollTop + clientHeight >= scrollHeight)
        ) {
          e.preventDefault();
        }
      }
    };

    container.addEventListener('touchstart', listeners.touchstart, { passive: false });
    container.addEventListener('touchmove', listeners.touchmove, { passive: false });
    container.addEventListener('wheel', listeners.wheel, { passive: false });

    this._scrollTraps.set(container, listeners);
  }

  removeScrollTrap(container) {
    const listeners = this._scrollTraps.get(container);
    if (!listeners) return;

    container.removeEventListener('touchstart', listeners.touchstart, { passive: false });
    container.removeEventListener('touchmove', listeners.touchmove, { passive: false });
    container.removeEventListener('wheel', listeners.wheel, { passive: false });

    this._scrollTraps.delete(container);
  }

  preventScroll(e) {
    e.stopPropagation();
    e.preventDefault();
  }

  /**
   * Toggles the visibility of a popup element, managing animations, accessibility attributes,
   * and event listeners for dialog and popover elements.
   *
   * @param {boolean} open - A boolean indicating whether to open (`true`) or close (`false`) the popup.
   * @returns {void}
   *
   * @description
   * This method handles the state of popup elements, ensuring proper animations,
   * accessibility compliance, and event management. It prevents redundant operations
   * if the popup is already in the desired state or if a transition is running.
   *
   * - Updates `aria-expanded` and `aria-hidden` attributes for accessibility.
   * - Manages animations for elements with specific animation types.
   * - Handles dialog elements (`HTMLDialogElement`) and popover elements.
   * - Dispatches custom events (`dialog:opened` or `dialog:closed`) for dialogs.
   * - Toggles backdrop visibility for elements with a backdrop.
   * - Adds or removes event listeners for body interactions when dialogs or popovers are active.
   *
   * @example
   * // Open the popup
   * togglePopup(true);
   *
   * @example
   * // Close the popup
   * togglePopup(false);
   */
  togglePopup(open) {
    // prevents redundant operations if the popup is already in the desired state or if a transition is running.
    if (this.isOpen() === open || this.transitionState === "running") return;

    if (PopupManager.currentOpenPopup && PopupManager.currentOpenPopup !== this && ['tree', 'menu', 'tooltip'].some(val => val === this.ariaHaspopup)) {
      PopupManager.currentOpenPopup.close();
    }
    /**
     * An array to store animation objects or configurations.
     * This can be used to manage and track animations within the application.
     *
     * @type {Array}
     */
    const animations = [];

    this.transitionState = "running";

    this.trigger.setAttribute('aria-expanded', open);
    this.otherTriggers.forEach(trig => {
      trig.setAttribute('aria-expanded', open);
    })

    this.targets.forEach((target) => {
      const animationType = target.dataset.animationType;
      target.hasAttribute('aria-expanded') && target.setAttribute('aria-expanded', (open).toString());
      target.hasAttribute('aria-hidden') && target.setAttribute('aria-hidden', (!open).toString());

      if (target instanceof HTMLDialogElement) {
        if (open) {
          if (target.classList.contains(Popup.CLASS_NAMES.IS_TOPLAYER)) {
            target.showModal();
          } else {
            target.show();
          }
        } else {
          target.close();
        }

        // Dispatch Custom Events
        const event = new CustomEvent(open ? 'dialog:opened' : 'dialog:closed', {
          detail: { dialogId: target.id }
        });
        window.dispatchEvent(event);

      } else if (target.hasAttribute('popover')) {
        target.togglePopover(open);
      }
      open && target.classList.toggle(COMMON_CLASS_NAMES.ACTIVE, open);

      if (target.classList.contains(Popup.CLASS_NAMES.HAS_BACKDROP) && this.backdrop) {
        this.backdrop.classList.toggle(COMMON_CLASS_NAMES.ACTIVE, open);
        DomUtils.waitForTransitionIfExists(this.backdrop).then(() => {
          this.backdrop.toggleAttribute('hidden', !open);
        });
      }

      if (target.classList.contains(DomAnimation.CLASS_NAMES.TARGET) && animationType != null) {
        const animation = new DomAnimation();
        const animPromise = new Promise(resolve => {
          if (animationType === DomAnimation.ANIMATION_TYPE.FADE_IN_OUT) {
            open ?
              animation.animate(target, 'fadeIn', {}, resolve) :
              animation.animate(target, 'fadeOut', {}, () => {
                target.classList.toggle(COMMON_CLASS_NAMES.ACTIVE, false);
                return resolve();
              });
          } else {
            open ? animation.animate(target, animationType, resolve) : animation.animate(target, animationType, () => {
              target.classList.toggle(COMMON_CLASS_NAMES.ACTIVE, open);
              return resolve();
            });
          }
        });
        animations.push(animPromise);
      } else {
        !open && target.classList.toggle(COMMON_CLASS_NAMES.ACTIVE, open);
      }

      if (target instanceof HTMLDialogElement || target.classList.contains(Popup.CLASS_NAMES.HAS_BACKDROP)) {
        open ?
          this.trapScroll(target) :
          this.removeScrollTrap(target);
      }
    })

    Promise.all(animations).then(() => {
      this.transitionState = "completed";
      if (Array.from(this.targets.values()).some(target => target instanceof HTMLDialogElement || target.hasAttribute('popover'))) {
        if (open) {
          document.body.addEventListener('click', this.boundDialogClick);
          document.body.addEventListener('keydown', this.boundKeydown, { passive: false });
        } else {
          document.body.removeEventListener('click', this.boundDialogClick);
          document.body.removeEventListener('keydown', this.boundKeydown, { passive: false });
        }
      }
    }).finally(() => {
      this.transitionState = "idle";
      PopupManager.currentOpenPopup = this
    });
  }

  listen() {
    switch (this.ariaHaspopup) {
      case 'tree':
      case 'menu':
        if (DomUtils.isTouchDevice() || this.trigger instanceof HTMLButtonElement) {
          this.trigger.addEventListener('click', this.handleClick.bind(this));
          this.trigger.addEventListener('keydown', this.handleKeydown.bind(this));
        } else {
          this.trigger.addEventListener('mouseenter', this.handleHover.bind(this));
          this.trigger.addEventListener('mouseleave', this.handleHover.bind(this));
          this.trigger.addEventListener('focusin', this.handleHover.bind(this));
          this.trigger.addEventListener('focusout', this.handleHover.bind(this));
        }
        break;
      case 'dialog':
        this.trigger.addEventListener('click', this.handleClick.bind(this));
        this.trigger.addEventListener('keydown', this.handleKeydown.bind(this));
        break;

      default:
        this.trigger.addEventListener('click', this.handleClick.bind(this));
        break;
    }
  }

  /** Add public open and close methods for convenience */
  open() {
    this.togglePopup(true);
  }

  close() {
    this.togglePopup(false);
  }

}

export class PopupManager {
  /** @type {Set(Popup)} */
  popups = new Set()
  constructor() {
    this.init();
  }

  /**
   * @type {Popup | null}
   * @description Stores a reference to the currently open popup element.
   * If no popup is open, the value is null.
   */
  static currentOpenPopup = null;

  init() {
    /**
     * A NodeList of elements that have the `aria-haspopup` attribute.
     * These elements typically indicate that they trigger a popup or dropdown menu.
     *
     * @constant {NodeList} popups
     */
    const popups = document.querySelectorAll(`[aria-haspopup]`);
    for (let i = 0; i < popups.length; i++) {
      const el = popups[i];
      const popup = new Popup(el);
      this.popups.add(popup);
    }

    this.popups.forEach(popup => popup.listen());
  }

}
